import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Flex, Grid, PageContent } from '@zillow/constellation';
import { Box, Button, Text } from '@zillow/constellation-10';

import { RMW_PATH } from '../../../../pathsManifest';

import { useGetListingSummary } from '../../../../hooks/queries/summary';

import {
  syncOnDataChangePropertyDetailsOverview,
  useGetPropertyDetailsOverview,
} from '../../../../hooks/queries/useGetPropertyDetailsOverview';

import { PaymentGatewayContext } from '../../../../components/PaymentGateway/context';

import { RELISTING_STEP_SUFFIX, STEP, STEP_DELIMITER } from '../../../../containers/CreateListingPage/constants';
import { DISPLAY_CONTEXT } from '../../../../components/PaymentGateway/constants';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';
import PropertyConstants from '../../../../constants/PropertyConstants';

import PropertiesActions from '../../../../actions/PropertiesActions';

import actions from '../../../../components/PaymentGateway/actions';

import { RMWContext } from '../../../../providers/RMWContext';

import { AppSelectors, RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { getFreemiumProductByType } from '../../../../utils/upsellUtils';
import accessUtils from '../../../../utils/accessUtils';

import { FeatureCard } from './components/FeatureCard';
import { getActiveProductTypeFreeFeatures, getActiveProductTypePremiumFeatures } from './constants';

const getCTAPath = (
  listingAlias: string,
  propertiesPage: string,
  status: string,
  canResumeModularListingCreation: boolean,
) => {
  if (canResumeModularListingCreation) {
    return `${propertiesPage}/${listingAlias}/create-listing`;
  }

  const listingPath = status !== LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE ? 'listing' : 'create-listing';

  return `${propertiesPage}/${listingAlias}/${listingPath}`;
};

const FeaturesOfPremium = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { paths } = useContext(RMWContext);

  const {
    pgState: { isResponseSuccess },
    reducerDispatch: pgReducerDispatch,
  } = useContext(PaymentGatewayContext);

  const {
    user,
    app: { cookieEnabledServices },
    properties: {
      currentProperty,
      currentProperty: {
        active,
        freemium: {
          activeProduct: { type: freemiumActiveProductType },
          isEnabled: isFreemiumEnabled,
          products: freemiumProducts,
        },
      },
    },
  } = useSelector((state) => state);

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const isDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const { data: listingSummaryData } = useGetListingSummary(listingAlias);
  const { data: propertyDetailsOverviewData } = useGetPropertyDetailsOverview(listingAlias);

  const [configObj, setConfigObj] = useState<{
    heading: string;
    bodyText: string | null;
    ctaUrl: string | null;
    ctaLabel: string | null;
  }>({
    heading: '',
    bodyText: '',
    ctaUrl: null,
    ctaLabel: '',
  });

  useEffect(() => {
    updateConfigObj();
  }, [isFreemiumEnabled, freemiumActiveProductType]);

  useEffect(() => {
    if (isResponseSuccess) {
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
    }
  }, [isResponseSuccess]);

  const { bodyText, ctaLabel, ctaUrl, heading } = configObj;

  const canAccessModularListingCreation = accessUtils.canAccessModularListing({
    currentProperty,
    cookieEnabledServices,
    user,
  });

  let { step } = listingSummaryData?.modularListingProgress || {};
  if (step === `${STEP.REVIEW}${STEP_DELIMITER}${RELISTING_STEP_SUFFIX}`.toLowerCase()) {
    step = step.split(STEP_DELIMITER)[0];
  }
  const canResumeModularListingCreation = canAccessModularListingCreation && step !== undefined;
  const status = listingSummaryData?.syndication?.overview?.displayStatus || '';

  const listingPath = getCTAPath(listingAlias, paths.propertiesPage, status, canResumeModularListingCreation);

  const isActiveProductTypeFree =
    isFreemiumEnabled && freemiumActiveProductType === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE;

  const isActiveProductTypePremium =
    isFreemiumEnabled && freemiumActiveProductType === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const isNotActiveAndFree = active === false && isActiveProductTypeFree;
  const isNotActiveAndPremium = active === false && isActiveProductTypePremium;

  const isActiveAndFree = active && isActiveProductTypeFree;
  const isActiveAndPremium = active && isActiveProductTypePremium;

  // @ts-expect-error
  const recsCount = propertyDetailsOverviewData?.recommendation?.unlockedCount;

  const upgradedProduct = useMemo(
    () => getFreemiumProductByType(freemiumProducts, PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM),
    [freemiumProducts],
  );

  useEffect(() => {
    if (propertyDetailsOverviewData) {
      syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
    }
  }, [propertyDetailsOverviewData]);

  const updateConfigObj = () => {
    const EXPLORE_PREMIUM_BENEFITS = 'Explore your premium benefits';
    const PREMIUM_PRICE_AND_DURATION = `${upgradedProduct?.price} for ${upgradedProduct?.duration}`;

    let copyConfigObj = { ...configObj };

    if (isNotActiveAndFree) {
      copyConfigObj = {
        heading: 'Get more from your listing with premium',
        bodyText: PREMIUM_PRICE_AND_DURATION,
        ctaLabel: 'List your Property',
        ctaUrl: listingPath,
      };
    }

    if (isNotActiveAndPremium) {
      copyConfigObj = {
        heading: EXPLORE_PREMIUM_BENEFITS,
        bodyText: null,
        ctaLabel: 'View your customized data',
        ctaUrl: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.PREMIUM_ANALYTICS}`,
      };
    }

    if (isActiveAndFree) {
      copyConfigObj = {
        heading: 'Get more from your listing with premium',
        bodyText: PREMIUM_PRICE_AND_DURATION,
        ctaLabel: 'Upgrade to premium',
        ctaUrl: null,
      };
    }

    if (isActiveAndPremium) {
      copyConfigObj = {
        heading: EXPLORE_PREMIUM_BENEFITS,
        bodyText: null,
        ctaLabel: 'View your customized data',
        ctaUrl: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.PREMIUM_ANALYTICS}`,
      };
    }

    setConfigObj({
      ...configObj,
      ...copyConfigObj,
    });
  };

  const handleClick = () => {
    if (isActiveAndFree) {
      setSelectedProductToPremium();
      updatePGDisplayContext(DISPLAY_CONTEXT.POSTING_PATH);
      updateDisplayPaymentGateway(true);

      return;
    }

    if (ctaUrl) {
      window.router.transitionTo(ctaUrl);
    }
  };

  const updatePGDisplayContext = (displayContext: string) => {
    actions.updateDisplayContext({
      reducerDispatch: pgReducerDispatch,
      displayContext,
    });
  };

  const setSelectedProductToPremium = () => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        ...currentProperty,
        selectedProductType: PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM,
      }),
    );
  };

  const updateDisplayPaymentGateway = (value: boolean) => {
    actions.updateDisplayPaymentGateway({
      reducerDispatch: pgReducerDispatch,
      displayPaymentGateway: value,
      // set to true to prevent activate endpoint from being called in payment gateway
      isPremiumMidfunnelPayment: true,
    });
  };

  const bodyContent = (
    <Box
      css={{
        backgroundColor: 'bg.action.impact.neutral.pressed',
        padding: { base: '{spacing.1200} {spacing.400}', lg: '{spacing.1200} {spacing.800}' },
      }}
    >
      <Flex display="flex" justifyContent="center" flexDirection="column" alignItems="center" marginBottom="md">
        <Text
          textStyle="heading-sm"
          asChild
          css={{ marginBottom: '400', color: 'text.onImpact.neutral-fixed', fontFamily: 'heading-alt' }}
        >
          <h4>{heading}</h4>
        </Text>
        <Text textStyle="body-bold" asChild css={{ color: 'text.onImpact.neutral-fixed' }}>
          <p>The edge you need to fill your vacancy fast</p>
        </Text>
        {bodyText && (
          <Text asChild css={{ color: 'text.onImpact.neutral-fixed' }}>
            <p>{bodyText}</p>
          </Text>
        )}
      </Flex>
      <Grid display="grid" gridTemplateColumns="repeat(4, 1fr)" columnGap="md" rowGap="sm">
        {isActiveProductTypePremium
          ? getActiveProductTypePremiumFeatures().map((item, index) => {
              const { heading, body, Icon, modalContent } = item;
              let headingCount = null;

              if (heading === 'Listing recommendations') {
                headingCount = recsCount;
              }

              return (
                <FeatureCard
                  key={index}
                  heading={heading}
                  headingCount={headingCount}
                  body={body}
                  Icon={Icon}
                  modalContent={modalContent}
                />
              );
            })
          : getActiveProductTypeFreeFeatures().map((item, index) => {
              const { heading, body, Icon, modalContent } = item;

              return <FeatureCard key={index} heading={heading} body={body} Icon={Icon} modalContent={modalContent} />;
            })}
      </Grid>
      <Flex display="flex" justifyContent="center">
        {ctaLabel && (
          <Button tone="brand" emphasis="filled" css={{ marginTop: '1200' }} onClick={handleClick}>
            {ctaLabel}
          </Button>
        )}
      </Flex>
    </Box>
  );

  return isDesktop ? (
    <PageContent>{bodyContent}</PageContent>
  ) : (
    <Box css={{ marginTop: { base: '-32px', xl: '0' } }}>{bodyContent}</Box>
  );
};

export default FeaturesOfPremium;
